// src/components/Visualization/TrendLine.js
import React, { useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';

const TrendLine = ({
  data = [],
  dataKey,
  xAxisKey = null,
  color = 'blue',
  showArea = true,
  showXAxis = false,
  showYAxis = false,
  showTooltip = false,
  lineWidth = 1,
  gradientFill = true,
  valueFormatter = (value) => value
}) => {
  // Cores para diferentes temas
  const colorMap = {
    blue: { line: '#1D4ED8', area: '#3B82F6', gradient: '#93C5FD' },
    green: { line: '#15803D', area: '#22C55E', gradient: '#86EFAC' },
    red: { line: '#B91C1C', area: '#EF4444', gradient: '#FCA5A5' },
    yellow: { line: '#A16207', area: '#EAB308', gradient: '#FEF08A' },
    purple: { line: '#7E22CE', area: '#A855F7', gradient: '#D8B4FE' },
    gray: { line: '#4B5563', area: '#6B7280', gradient: '#D1D5DB' }
  };

  const theme = colorMap[color] || colorMap.blue;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const formattedValue = valueFormatter(value);
      return (
        <div className="bg-white p-2 text-xs shadow-md border border-gray-200 rounded">
          <p className="font-medium">{formattedValue}</p>
        </div>
      );
    }
    return null;
  };

  // ✅ useMemo no topo, SEM condicional
  const { minValue, maxValue } = useMemo(() => {
    if (!data.length || !dataKey) return { minValue: 0, maxValue: 0 };

    let min = Infinity;
    let max = -Infinity;

    data.forEach(item => {
      const value = Number(item[dataKey]);
      if (!isNaN(value)) {
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    });

    const padding = (max - min) * 0.1;
    return {
      minValue: min - padding,
      maxValue: max + padding
    };
  }, [data, dataKey]);

  // ✅ Agora pode retornar null, após hooks
  if (!data.length || !dataKey) return null;

  return (
    <ResponsiveContainer width="100%" height="100%">
      {showArea ? (
        <AreaChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          {showXAxis && <XAxis dataKey={xAxisKey} hide={!showXAxis} />}
          {showYAxis && (
            <YAxis domain={[minValue, maxValue]} hide={!showYAxis} />
          )}
          {showTooltip && <Tooltip content={<CustomTooltip />} />}
          <defs>
            <linearGradient id={`color${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={theme.area} stopOpacity={0.8} />
              <stop offset="95%" stopColor={theme.gradient} stopOpacity={0.2} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={theme.line}
            strokeWidth={lineWidth}
            fill={gradientFill ? `url(#color${color})` : theme.area}
            fillOpacity={0.5}
            activeDot={{ r: 4, strokeWidth: 1 }}
          />
        </AreaChart>
      ) : (
        <LineChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          {showXAxis && <XAxis dataKey={xAxisKey} hide={!showXAxis} />}
          {showYAxis && (
            <YAxis domain={[minValue, maxValue]} hide={!showYAxis} />
          )}
          {showTooltip && <Tooltip content={<CustomTooltip />} />}
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={theme.line}
            strokeWidth={lineWidth}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 1 }}
          />
        </LineChart>
      )}
    </ResponsiveContainer>
  );
};

export default TrendLine;
